import {
  getStartOfRangeHorizon,
  i18n,
  MenuBar,
  RANGE_HORIZON_DEFAULT,
  RangeHorizon,
  rangeToResampleMapping,
  RESAMPLE_DEFAULT,
  SeriesType,
  ViewController,
} from '@toggle/chart';
import { useResizeObserver } from '@toggle/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChartArea } from '~/views/chart-v2-playground/components/chart-area/ChartArea';

import * as S from './ChartV2Playground.styles';
import { FullscreenModal } from './components/fullscreen-modal/FullscreenModal';
import { Legend } from './components/legend/Legend';
import { SearchModal } from './components/search-modal/SearchModal';
import { useChartV2 } from './hooks/useChartV2';

export const ChartV2Playground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedRange, setSelectedRange] = useState(RANGE_HORIZON_DEFAULT);
  const [selectedResample, setSelectedResample] = useState(RESAMPLE_DEFAULT);
  const {
    isSearchOpen,
    openSearch,
    closeSearch,
    activeAssets,
    setActiveAssets,
    initialData,
  } = useChartV2();
  const [selectedSeriesType, setSelectedSeriesType] =
    useState<SeriesType>('line');
  const [horizonRanges, setHorizonRanges] = useState<RangeHorizon[]>([]);

  const { width, height } = useResizeObserver({ ref: containerRef });
  const navigate = useNavigate();

  const closeHandler = () => {
    navigate(-1);
  };

  const onRangeChange = (range: RangeHorizon) => {
    setSelectedRange(range);
    setSelectedResample(rangeToResampleMapping[range]);
  };

  useEffect(() => {
    if (initialData.length) {
      const endDate = new Date(initialData[initialData.length - 1].time);
      const startDate = new Date(initialData[0].time);
      const filteredHorizons = Object.values(RangeHorizon).filter(range => {
        const start = getStartOfRangeHorizon(range, endDate);
        return start === undefined || start >= startDate.getTime();
      });

      const defaultHorizon = filteredHorizons.find(
        horizon => horizon === RANGE_HORIZON_DEFAULT
      );
      const horizonRange =
        defaultHorizon ||
        filteredHorizons[filteredHorizons.length - 2] ||
        filteredHorizons[filteredHorizons.length - 1];
      onRangeChange(horizonRange);
      setHorizonRanges(filteredHorizons);
    }
  }, [initialData]);

  return (
    <>
      {isSearchOpen && (
        <SearchModal
          activeAssets={activeAssets}
          setActiveAssets={setActiveAssets}
          onClose={closeSearch}
        />
      )}
      <I18nextProvider i18n={i18n}>
        <FullscreenModal
          header={
            <MenuBar
              searchLabel={activeAssets[0]?.ticker}
              onSearch={openSearch}
            />
          }
          onClose={closeHandler}
        >
          <ViewController
            horizonRanges={horizonRanges}
            selectedRange={selectedRange}
            selectedResample={selectedResample}
            selectedSeriesType={selectedSeriesType}
            onRangeChange={onRangeChange}
            onResampleChange={setSelectedResample}
            onChartTypeChange={setSelectedSeriesType}
          />
          <Legend activeAssets={activeAssets} />
          <S.ChartAreaWrapper ref={containerRef}>
            {selectedRange && !!initialData.length && (
              <ChartArea
                data={initialData}
                options={{
                  width,
                  height,
                  gutters: {
                    x: 40,
                    y: 40,
                  },
                }}
                selectedRange={selectedRange}
                selectedSeriesType={selectedSeriesType}
              />
            )}
          </S.ChartAreaWrapper>
        </FullscreenModal>
      </I18nextProvider>
    </>
  );
};
